import Order from "../../Models/Order";
import OrderOverviewModal from "./OrderOverviewModal";
import { fetchNextOrderStatus } from "../../HTTPRequests/OrderRequests";
import Loader from "../../DomUtilities/Loader";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import OrderPrintHandler from "../OrderPrintHandler";
import Mediator from "../../Utilities/Mediator";
import { isNumber } from "../../Validations/Validations";
import OrderUpdateModal from "./Update/OrderUpdateModal";

export default class OrdersScanHandler {
	table: HTMLElement;
	readonly ARRIVED_PRINT_STATUS = "3";
	code: string;
	readonly SCANNER_THRESHOLD_MILLISECONDS: number = 100;
	currentTimer: NodeJS.Timeout;
	connection: HubConnection;
	username: string;
	isDevEnv: boolean;
	isScanEnabled: boolean;

	constructor() {
		this.table = document.getElementById('orders');
		this.username = this.table.dataset.username;
		this.code = "";
		this.isScanEnabled = true;

		this.init();
	}

	private init(): void {
		const ENVIROMENTS: string[] = ["Local", "Development"];
		const environment: string = this.table.dataset.environment;
		this.isDevEnv = ENVIROMENTS.includes(environment);

		this.addScannerEventsListeners();
		this.connectToSignalRCheckPointHub();
		Mediator.on("scanEnabled", (isEnabled: boolean) => {
			this.isScanEnabled = isEnabled;
		});
	}

	private addScannerEventsListeners() {
		window.onkeydown = (event: KeyboardEvent) => {
			if (this.isScanEnabled) {
				this.processInputKeyForScan(event.key);
			}
		}
	}

	private processInputKeyForScan(inputCode: string) {
		if (isNumber(inputCode)) {
			this.code += inputCode;
			if (this.code.length === 1) {
				this.currentTimer = setTimeout(() => this.sendRequestAndResetCode(), this.SCANNER_THRESHOLD_MILLISECONDS);
			}
		} else if (inputCode.toLowerCase().includes("enter") && this.code.length > 0) {
			clearTimeout(this.currentTimer);
			this.sendRequestAndResetCode();
		}
	}

	private sendRequestAndResetCode() {
		Loader.show();

		if (this.connection.state != HubConnectionState.Connected) {
			this.startSignalRConnection(this.connection, this.username);
		}

		fetchNextOrderStatus(this.code).then().finally(() => Loader.hide());
		this.code = "";
	}

	private connectToSignalRCheckPointHub() {
		const url: string = this.table.dataset.messageProcessingUrl;
		const logLevel = this.isDevEnv ? LogLevel.Debug : LogLevel.None;
		this.connection = new HubConnectionBuilder()
			.withUrl(`${url}/checkpointHub`)
			.configureLogging(logLevel)
			.build();

		this.connection.on("OrderChecked", (order: Order) => {
			const modals: NodeListOf<HTMLDivElement> = document.querySelectorAll("#order-overview");
			if (modals.length) {
				modals.forEach(modal => modal.remove());
			}

			if (order.status == this.ARRIVED_PRINT_STATUS) {
				new OrderPrintHandler(order.id, null);
				new OrderUpdateModal().initUpdateModal(order.id);
			} else {
				new OrderOverviewModal().fetchOverviewModal(order.id);
			}

			Mediator.emit("orderOverviewOperationFinished");
		});

		this.startSignalRConnection(this.connection, this.username);

		if (this.isDevEnv) {
			console.log("connection: ", this.connection);
		}

		this.connection.onclose = (err) => {
			if (this.isDevEnv) {
				console.log("err from onclose: ", err);
				console.log("connection from onclose: ", this.connection);
			}
		}

		this.connection.onreconnecting = (err) => {
			if (this.isDevEnv) {
				console.log("err from onreconnecting: ", err);
				console.log("connection from onreconnecting: ", this.connection);
			}
		}
	}

	private async startSignalRConnection(connection: HubConnection, username: string) {
		try {
			await connection.start().then(() => connection.invoke("Subscribe", username));
		} catch (error) {
			if (this.isDevEnv) {
				console.log("error at start connection ", error);
			}
			setTimeout(this.startSignalRConnection, 5000);
		}
	}
}
